import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation, withTranslation } from 'react-i18next';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import Modal from 'react-modal';
import { createRoot } from 'react-dom/client';
import Parser from 'html-react-parser';
import { ProgressBar, Card, Button } from 'shared/components';
import './TrainingModule.scss';

const isPdf = (task) => {
  if (isEmpty(task)) return false;

  const extension = task.documentUrl.substring(task.documentUrl.lastIndexOf('/') + 1);
  return /(pdf)$/gi.test(extension);
};

const getObjectType = (task) => (isPdf(task) ? 'application/pdf' : 'text/html');

class TrainingModule extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      task: {},
    };
    this.pdfRoot = null;
    this.fallbackRoot = null;

    this.closeModal = this.closeModal.bind(this);
    this.openTraining = this.openTraining.bind(this);
    this.onTaskComplete = this.onTaskComplete.bind(this);
    this.nextTask = this.nextTask.bind(this);
    this.onIframeLoad = this.onIframeLoad.bind(this);
  }

  onTaskComplete() {
    const { module } = this.props;
    const { task } = this.state;
    task.completed = true;

    this.setState({
      task,
    });

    module.submodules = module.submodules.map((submodule) => {
      const sub = submodule;
      sub.tasks = sub.tasks.map((t) => {
        if (t.id === task.id) {
          return task;
        }
        return t;
      });
      return sub;
    });

    this.props.onComplete(task.id);
  }

  tasks = flatMap(this.props.module.submodules, (submodule) => submodule.tasks);

  closeModal() {
    this.setState({
      modalIsOpen: false,
      task: {},
    });
  }

  openTraining(task) {
    const idx = this.tasks.findIndex((t) => t.id === task.id);
    if (idx && !this.tasks[idx - 1].completed) {
      return;
    }

    this.setState({
      modalIsOpen: true,
      task,
    });
    setTimeout(() => this.onResetObject(task), 100);
  }

  onResetObject(task) {
    const viewer = document.getElementById('embeddedContent');
    const parent = document.getElementsByClassName('embed-responsive-item')[0];
    const taskIsPdf = isPdf(task);
    // const parent = viewer.parentElement;
    const newViewer = document.createElement(taskIsPdf ? 'object' : 'iframe');

    if (taskIsPdf) {
      newViewer.setAttribute('data', `${task.documentUrl}?#scrollbar=0&toolbar=0&navpanes=0`);
      newViewer.type = getObjectType(task);
      newViewer.width = '100%';
      newViewer.height = '100%';
      if (this.pdfRoot) {
        this.pdfRoot.unmount();
      }
      this.pdfRoot = createRoot(newViewer);
      this.pdfRoot.render(<FallBackContent task={task} />);
    } else {
      newViewer.setAttribute('src', task.documentUrl);
    }

    newViewer.id = 'embeddedContent';
    newViewer.addEventListener('load', this.onIframeLoad);

    if (viewer) {
      parent.removeChild(viewer);
    }
    parent.appendChild(newViewer);
  }

  onIframeLoad() {
    let iframe = document.getElementById('embeddedContent');

    if (iframe && !isPdf(this.state.task)) {
      let parent = iframe.parentElement;
      const notSupported = document.createElement('div');
      notSupported.id = 'embeddedContent';
      if (this.fallbackRoot) {
        this.fallbackRoot.unmount();
      }
      this.fallbackRoot = createRoot(notSupported);
      this.fallbackRoot.render(<FallBackContent task={this.state.task} />);

      setTimeout(() => {
        try {
          let doc = iframe.contentWindow
            ? iframe.contentWindow.frames.length
            : iframe.contentWindow;
          if (!doc) {
            throw 'iframe not loaded';
          }
        } catch (e) {
          if (e === 'iframe not loaded') {
            parent.removeChild(iframe);
            parent.appendChild(notSupported);
            console.log('ERROR!!!');
          }
        }
      }, 1000);
    }
  }

  nextTask() {
    const idx = this.tasks.findIndex((task) => task.id === this.state.task.id);
    if (idx < this.tasks.length - 1) {
      this.setState({
        task: this.tasks[idx + 1],
      });
      this.onResetObject(this.tasks[idx + 1]);
      return;
    }
    this.closeModal();
  }

  render() {
    const completed = this.props.module.submodules.filter((submodule) =>
      submodule.tasks.every((task) => task.completed)
    );
    const { module, program } = this.props;
    const { task, modalIsOpen } = this.state;

    return (
      <div className="training-module">
        <h4>{module.name}</h4>
        <div>{program.name}</div>
        <ProgressBar
          current={completed.length}
          total={module.submodules.length}
          labelAddon={this.props.t('common.labels.completed')}
        />
        <p className="font-size-10">{this.props.t('training.moduleCopy')}</p>

        <div className="submodules-list">
          {module.submodules.map((submodule) => (
            <Card header={submodule.name} key={submodule.id}>
              {submodule.tasks.map((t) => (
                <TrainingTask task={t} key={t.id} onClick={() => this.openTraining(t)} />
              ))}
            </Card>
          ))}
        </div>
        <Link to="/training">
          <Button
            type="btn-outline"
            text={this.props.t('training.backToTrainingDashboard')}
            onClick={() => {}}
          />
        </Link>
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={this.closeModal}
          shouldCloseOnOverlayClick={false}
          ariaHideApp={false}
          contentLabel="Modal"
          className="training-material-modal"
          role="dialog">
          <div className="modal-header">
            <i
              className="material-icons"
              onKeyPress={(e) => e.which === 27 && this.closeModal()}
              onClick={this.closeModal}
              role="button"
              tabIndex="0">
              clear
            </i>
            <span>{task.name}</span>
          </div>
          {!isEmpty(task) && (
            <div className="modal-content embed-responsive">
              <div className="embed-responsive-item"></div>
            </div>
          )}
          <div className="modal-footer">
            {task.completed ? (
              <div className="justify-content-between w-100 d-flex align-items-center">
                <label className="text-green text-strong mb-0">
                  <i className="material-icons pe-1">done</i>
                  <span>{this.props.t('training.readAndUnderstandLabel')}</span>
                </label>
                <Button
                  type="btn-primary"
                  className="btn-small"
                  text={this.props.t('training.nextSection')}
                  onClick={this.nextTask}
                />
              </div>
            ) : (
              <Button
                type="btn-primary"
                className="btn-small"
                text={this.props.t('training.readAndUnderstandLabel')}
                onClick={this.onTaskComplete}
              />
            )}
          </div>
        </Modal>
      </div>
    );
  }
}

TrainingModule.propTypes = {
  module: PropTypes.shape({
    submodules: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
};

export const TrainingTask = (props) => (
  <div
    className="training-task"
    onKeyPress={(e) => e.which === 13 && props.onClick()}
    onClick={props.onClick}
    role="button"
    tabIndex="0">
    <div>
      <i className="material-icons md-18 pe-2">insert_drive_file</i>
      <span>{props.task.name}</span>
    </div>
    <i className={`material-icons md-20 checkmark ${props.task.completed && 'completed'}`}>done</i>
  </div>
);

export const FallBackContent = (props) => {
  const { t } = useTranslation();
  return (
    <div className="p-4">
      <h3>
        {Parser(
          t('training.notLoadingMessage', {
            link: props.task.documentUrl,
          })
        )}
      </h3>
    </div>
  );
};

TrainingTask.propTypes = {
  onClick: PropTypes.func.isRequired,
  task: PropTypes.shape({
    name: PropTypes.string,
    completed: PropTypes.bool,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(TrainingModule);
